/**
 * Shape Tool Settings Panel
 * Shape type picker + settings for fill, stroke, stroke width, and border radius
 */

import React from 'react';
import { Square, Circle, Triangle, Hexagon, Star, Pentagon } from 'lucide-react';
import { useToolbarStore } from '@/stores/toolbarStore';
import type { ShapeToolType } from '@/types/tools';
import { ColorSwatch } from '@/components/ui/BrushPicker';
import { solid } from '@/types/brush';

const SHAPE_TYPES: { id: ShapeToolType; label: string; icon: typeof Square }[] = [
  { id: 'rectangle', label: 'Rect', icon: Square },
  { id: 'circle', label: 'Circle', icon: Circle },
  { id: 'triangle', label: 'Tri', icon: Triangle },
  { id: 'hexagon', label: 'Hex', icon: Hexagon },
  { id: 'pentagon', label: 'Pent', icon: Pentagon },
  { id: 'star', label: 'Star', icon: Star },
];

export function ShapeToolSettings() {
  const { shapeDefaults, updateShapeDefaults } = useToolbarStore();

  // Only show border radius for rectangles
  const showBorderRadius = shapeDefaults.shapeType === 'rectangle';

  return (
    <div className="space-y-3">
      {/* Shape type picker */}
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-fg-subtle mb-1.5">
          Shape
        </label>
        <div className="grid grid-cols-3 gap-1">
          {SHAPE_TYPES.map((shape) => {
            const Icon = shape.icon;
            const isSelected = shapeDefaults.shapeType === shape.id;
            return (
              <button
                key={shape.id}
                onClick={() => updateShapeDefaults({ shapeType: shape.id })}
                className={`
                  flex flex-col items-center gap-1 p-2 rounded-lg transition-colors
                  ${isSelected
                    ? 'bg-primary/20 text-primary border border-primary/30'
                    : 'bg-white/5 text-fg-muted hover:bg-white/10 hover:text-fg border border-transparent'
                  }
                `}
                title={shape.label}
              >
                <Icon size={16} strokeWidth={1.5} />
                <span className="text-[9px]">{shape.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Fill color */}
      <div className="min-w-0">
        <label className="block text-[10px] uppercase tracking-wider text-fg-subtle mb-1">
          Fill
        </label>
        <ColorSwatch
          value={shapeDefaults.fill}
          onChange={(fill) => updateShapeDefaults({ fill })}
        />
      </div>

      {/* Stroke color */}
      <div className="min-w-0">
        <label className="block text-[10px] uppercase tracking-wider text-fg-subtle mb-1">
          Stroke
        </label>
        <ColorSwatch
          value={shapeDefaults.stroke ?? solid('#000000')}
          onChange={(stroke) => updateShapeDefaults({ stroke })}
        />
      </div>

      {/* Stroke width */}
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-fg-subtle mb-1">
          Stroke Width
        </label>
        <input
          type="number"
          value={shapeDefaults.strokeWidth}
          onChange={(e) => updateShapeDefaults({ strokeWidth: parseInt(e.target.value) || 0 })}
          min={0}
          max={50}
          className="w-full bg-navy border border-white/10 rounded-lg px-2 py-1.5 text-sm text-fg focus:outline-none focus:border-primary/50"
        />
      </div>

      {/* Border radius (only for rectangle) */}
      {showBorderRadius && (
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-fg-subtle mb-1">
            Corner Radius
          </label>
          <input
            type="number"
            value={shapeDefaults.borderRadius}
            onChange={(e) => updateShapeDefaults({ borderRadius: parseInt(e.target.value) || 0 })}
            min={0}
            max={200}
            className="w-full bg-navy border border-white/10 rounded-lg px-2 py-1.5 text-sm text-fg focus:outline-none focus:border-primary/50"
          />
        </div>
      )}
    </div>
  );
}
